import { toast } from 'sonner';
import { Camera, FileUp, Loader2, Upload, X } from 'lucide-react';
import { type ChangeEvent, type DragEvent, useCallback, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { formatFileSize } from '../utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import { useUploadDocument } from '../api/uploaded-documents-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB, UPLOADED_DOCUMENT_TYPES } from '../constants';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface UploadDocumentDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /**
   * When omitted, the document is uploaded into the Inbox (no patient).
   * It can be assigned to a patient later via the Inbox surface.
   */
  patientId?: string | null;
}

export function UploadDocumentDialog({ open, patientId, onOpenChange }: UploadDocumentDialogProps) {
  const isInboxUpload = !patientId;
  const isPersonal = useIsPersonalEdition();
  const { today } = useOrgDateFormat();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [documentDate, setDocumentDate] = useState<string>(() => today());
  const [notes, setNotes] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const uploadMutation = useUploadDocument();

  const resetForm = useCallback(() => {
    setFile(null);
    setName('');
    setDocumentType('');
    setDocumentDate(today());
    setNotes('');
    setIsDragging(false);
  }, [today]);

  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  const handleFileSelect = useCallback(
    (selectedFile: File) => {
      if (!isAcceptedFile(selectedFile)) {
        toast.error(`Unsupported file type. Accepted: ${ACCEPTED_FILE_EXTENSIONS.join(', ')}`);
        return;
      }
      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }
      setFile(selectedFile);
      if (!name) {
        setName(fileNameWithoutExtension(selectedFile.name));
      }
    },
    [name],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        handleFileSelect(droppedFile);
      }
    },
    [handleFileSelect],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        handleFileSelect(selectedFile);
      }
    },
    [handleFileSelect],
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  }, []);

  const canSubmit = file && name.trim() && documentType && documentDate;

  const handleSubmit = async () => {
    if (!file || !canSubmit) return;
    try {
      await uploadMutation.mutateAsync({
        file,
        name: name.trim(),
        document_type: documentType,
        document_date: documentDate,
        patient_id: patientId ?? null,
        notes: notes.trim() || undefined,
      });
      toast.success(isInboxUpload ? 'Uploaded to Inbox' : 'Document uploaded successfully');
      handleClose();
    } catch {
      toast.error('Failed to upload document. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {isInboxUpload ? 'Upload to Inbox' : 'Upload Document'}
          </DialogTitle>
          <DialogDescription>
            {isInboxUpload
              ? 'Upload a document into the Inbox. You can assign it to a patient chart afterwards.'
              : 'Upload an external document for this patient. Text extraction will begin automatically.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Drop zone / File display */}
          {!file ? (
            isPersonal ? (
              // Drag handlers on the outer wrapper so a drop anywhere across
              // the two-tile zone counts — not only on the left Browse tile.
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={cn(
                  'grid grid-cols-2 gap-3 rounded-2xl transition',
                  isDragging && 'bg-primary/5 ring-2 ring-primary',
                )}>
                <button
                  type="button"
                  className="group flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-muted-foreground/25 p-4 text-center transition hover:border-muted-foreground/50"
                  onClick={() => fileInputRef.current?.click()}>
                  <FileUp className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-semibold">Browse</p>
                    <p className="text-xs text-muted-foreground">PDF, JPG, PNG up to {MAX_FILE_SIZE_MB} MB</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
                    onChange={handleInputChange}
                  />
                </button>
                <button
                  type="button"
                  className="group flex aspect-square flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-muted-foreground/25 p-4 text-center transition hover:border-muted-foreground/50"
                  onClick={() => cameraInputRef.current?.click()}>
                  <Camera className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-semibold">Take Picture</p>
                    <p className="text-xs text-muted-foreground">Use your camera</p>
                  </div>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    className="hidden"
                    capture="environment"
                    accept="image/*,image/heic,image/heif"
                    onChange={handleInputChange}
                  />
                </button>
              </div>
            ) : (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors cursor-pointer',
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-muted-foreground/25 hover:border-muted-foreground/50',
                )}>
                <FileUp className="h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm font-medium">Drop a file here or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, JPG, PNG, DOC, DOCX up to {MAX_FILE_SIZE_MB} MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
                  onChange={handleInputChange}
                />
              </div>
            )
          ) : (
            <div className="flex items-center gap-3 rounded-lg border p-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                <FileUp className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleRemoveFile}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="doc-name">Name</Label>
            <Input id="doc-name" value={name} placeholder="Document name" onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label>Document Type</Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type..." />
              </SelectTrigger>
              <SelectContent>
                {UPLOADED_DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Document Date */}
          <div className="space-y-2">
            <Label>Document Date</Label>
            <DatePicker value={documentDate} onChange={setDocumentDate} />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="doc-notes">Notes (optional)</Label>
            <Textarea
              id="doc-notes"
              rows={2}
              value={notes}
              placeholder="Additional notes about this document..."
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={uploadMutation.isPending} onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={!canSubmit || uploadMutation.isPending} onClick={handleSubmit}>
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fileNameWithoutExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.slice(0, lastDot) : name;
}

// iOS Safari camera capture can deliver HEIC/HEIF files even when the user
// expected a JPEG. Accept them here so the camera tile doesn't reject what
// the browser just handed over; the backend re-encodes on extraction.
const CAMERA_IMAGE_EXTENSIONS = ['.heic', '.heif'] as const;

function isAcceptedFile(file: File): boolean {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  if (ACCEPTED_FILE_EXTENSIONS.includes(ext)) return true;
  if ((CAMERA_IMAGE_EXTENSIONS as readonly string[]).includes(ext)) return true;
  // Camera captures occasionally arrive with an empty filename extension but
  // a valid image MIME — fall back to the type sniff.
  return file.type.startsWith('image/');
}
